import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import Glyph from 'component/utils/Glyph'
import 'container/priceBanner/styles.css';
import Modal from 'component/Modal/GenericModal'
import ExclusionModalPriceBanner from 'component/priceBanner/general/ExclusionModalPriceBanner'
import EditExtrasModal from 'component/priceBanner/priceBannerProduct/EditExtrasModal'
import useScrollShadow from 'utils/hooks/useScrollShadow'
import { isClubeLeroyThemeCode, isCmpmThemeCode } from 'utils/priceBannerCsvWithStores'

const TableProduct = ({
  handleConfirmDelete,
  records,
  cdDynamicList,
  setCdDynamicList,
  translate,
  removeSelected,
  addSelectedItem,
  setSelectedItemsData,
  user,
  onRefresh,
  onEditExtrasSuccess,
  onEditExtrasModalClosed
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState(null);
  const tableRef = useRef(null)
  const tbodyRef = useRef(null)
  const [height, setHeight] = useState(0)
  const PAGINATION_HEIGHT = 71
  const TOP_ACTIONS = 95
  const TOP_BAR = 67 + 50
  const isSelectionMode = cdDynamicList.length > 0;

  const [columnWidths, setColumnWidths] = useState({
    checkbox: 50,
    lmcode: 100,
    description: 250,
    listName: 300,
    theme: 150,
    stores: 200,
    edit: 80,
    delete: 80
  })
  const [tableMinWidth, setTableMinWidth] = useState('100%')
  const [isResizing, setIsResizing] = useState(false)
  const [resizingColumn, setResizingColumn] = useState(null)
  const [startX, setStartX] = useState(0)
  const [startWidth, setStartWidth] = useState(0)

  const resizableStyles = useMemo(() => ({
    table: {
      tableLayout: 'fixed',
      width: 'auto',
      minWidth: tableMinWidth
    },
    cell: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      position: 'relative',
      padding: '8px'
    },
    headerCell: {
      position: 'relative',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      cursor: 'default'
    }
  }), [tableMinWidth])

  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault()
    setIsResizing(true)
    setResizingColumn(columnKey)
    setStartX(e.clientX)
    setStartWidth(columnWidths[columnKey])
    document.body.style.cursor = 'e-resize'
    document.body.style.userSelect = 'none'
  }, [columnWidths])

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !resizingColumn) return
    const diff = e.clientX - startX
    const newWidth = Math.max(15, startWidth + diff)
    setColumnWidths(prev => {
      const updatedWidths = {
        ...prev,
        [resizingColumn]: newWidth
      }
      const totalWidth = Object.values(updatedWidths).reduce((sum, width) => sum + width, 0)
      const tableWrapper = document.querySelector('.table-scroll-wrapper')
      const containerWidth = tableWrapper ? tableWrapper.clientWidth : 800
      if (totalWidth > containerWidth) {
        const percentage = (totalWidth / containerWidth) * 100
        const newMinWidth = Math.min(percentage + 10, 200)
        setTableMinWidth(`${newMinWidth}%`)
      } else {
        setTableMinWidth('100%')
      }
      return updatedWidths
    })
    const table = document.querySelector('.mission-block-table-new')
    if (table) {
      table.style.position = 'relative'
      let resizeLine = document.getElementById('resize-line')
      if (!resizeLine) {
        resizeLine = document.createElement('div')
        resizeLine.id = 'resize-line'
        resizeLine.style.cssText = `
          position: absolute;
          top: 0;
          bottom: 0;
          width: 1px;
          background-color: #007bff;
          z-index: 1000;
          pointer-events: none;
          opacity: 0.8;
        `
        table.appendChild(resizeLine)
      }
      const tableRect = table.getBoundingClientRect()
      const mouseX = e.clientX - tableRect.left
      const resizingHeader = table.querySelector(`th[data-column="${resizingColumn}"]`)
      if (resizingHeader) {
        const headerRect = resizingHeader.getBoundingClientRect()
        const headerLeft = headerRect.left - tableRect.left
        const headerRight = headerLeft + headerRect.width
        const minColumnWidth = 15
        const minX = headerRight - minColumnWidth
        const limitedX = Math.max(minX, mouseX)
        resizeLine.style.left = `${limitedX}px`
      } else {
        resizeLine.style.left = `${mouseX}px`
      }
    }
  }, [isResizing, resizingColumn, startX, startWidth])

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false)
      setResizingColumn(null)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      const resizeLine = document.getElementById('resize-line')
      if (resizeLine) {
        resizeLine.remove()
      }
    }
  }, [isResizing])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  const calculateHeight = useCallback(() => {
    setHeight(window.innerHeight)
  }, [])

  useEffect(() => {
    calculateHeight()
    window.addEventListener("resize", calculateHeight)
    return () => {
      window.removeEventListener("resize", calculateHeight)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [calculateHeight, handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (!tbodyRef.current || height === 0) return;
    const responsiveHeight = height < 868
      ? height - (PAGINATION_HEIGHT + TOP_ACTIONS + TOP_BAR + 100)
      : 500;
    tbodyRef.current.style.maxHeight = `${responsiveHeight}px`
  }, [height])

  const handleOpenBranchesModal = useCallback((branchesList) => {
    setModalOpen(false);
    setTimeout(() => {
      setModalData(branchesList);
      setModalOpen(true);
    }, 10);
  }, []);

  const renderBranches = useCallback((record) => {
    const grouped = record.branchNamesGrouped;
    const { branches } = record;
    
    if (Array.isArray(grouped) && grouped.length >= 5) {
      return (
        <span>{translate('price.banner.all')}</span>
      );
    }

    const regionNames = Array.isArray(grouped) ? grouped : [];
    
    const individualBranches = Array.isArray(branches) 
      ? branches.filter(b => !regionNames.includes(b.dsRegion))
      : [];
    
    const allItems = [...regionNames, ...individualBranches.map(b => formatBranchDisplay(b))];
    
    if (allItems.length === 0) {
      return "-";
    }
    
    if (allItems.length === 1) {
      return allItems[0];
    }
    
    return (
      <span
        className="clickable-green"
        onClick={() => handleOpenBranchesModal(allItems)}
      >
        {translate('customSelect.multiLabel')}
      </span>
    );
  }, [handleOpenBranchesModal, translate, formatBranchDisplay]);

  const formatBranchDisplay = useCallback((branches) => {
    if (!branches || !branches.idBranch || !branches.dsBranch) return "-";
    const id = branches.idBranch.slice(2);
    const name = branches.dsBranch
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `${id} - ${name}`;
  }, []);

  const ResizableColumn = ({ columnKey, width, children, className = '', onClick = null, isActionColumn = false, isCheckboxColumn = false }) => {
    if (isActionColumn || isCheckboxColumn) {
      return (
        <th
          data-column={columnKey}
          style={{
            ...resizableStyles.headerCell,
            ...resizableStyles.cell,
            width,
            minWidth: '15px',
            position: 'relative'
          }}
          className={className}
          onClick={onClick}
        >
          {children}
        </th>
      );
    }

    return (
      <th
        data-column={columnKey}
        style={{
          ...resizableStyles.headerCell,
          ...resizableStyles.cell,
          width,
          minWidth: '15px',
          position: 'relative'
        }}
        className={`${className} resizable-header`}
        onClick={onClick}
      >
        {children}
        <div
          className="resize-handle"
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            height: '100%',
            width: '15px',
            cursor: 'e-resize',
            backgroundColor: 'transparent',
            zIndex: 10,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: '2px'
          }}
          onMouseDown={(e) => handleMouseDown(e, columnKey)}
          title="Arraste para redimensionar"
        >
          <svg
            className="resize-icon"
            style={{
              width: '15px',
              height: '6px',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              pointerEvents: 'none'
            }}
            viewBox="0 0 28 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0 6L10 11.7735V0.226497L0 6ZM28 6L18 0.226497V11.7735L28 6ZM9 7H19V5H9V7Z" fill="#3C3738" />
          </svg>
        </div>
      </th>
    );
  }

  ResizableColumn.propTypes = {
    columnKey: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    onClick: PropTypes.func,
    isActionColumn: PropTypes.bool,
    isCheckboxColumn: PropTypes.bool
  }

  const renderHeader = useCallback(() => {
    let checked = false
    let setAll = true
    let checkedClassName = "stock-transfer-checkbox"
    const cdDynamicListLength = cdDynamicList.length
    if (cdDynamicListLength) {
      if (cdDynamicListLength < records.length) {
        checked = true
        checkedClassName = "stock-transfer-checked-mid"
      } else {
        checked = true
        setAll = false
      }
    }

    return (
      <tr className='pictograms-tr'>
        <ResizableColumn
          columnKey="checkbox"
          width={columnWidths.checkbox}
          className='text-align-center'
          isCheckboxColumn={true}
          onClick={() => {
            if (setAll) {
              const productIds = records.map(i => i.cdProduct || i.id || i.cdPricebannerThemesList);
              setCdDynamicList(productIds);
              if (setSelectedItemsData) {
                setSelectedItemsData(records);
              }
            } else {
              setCdDynamicList([]);
              if (setSelectedItemsData) {
                setSelectedItemsData([]);
              }
            }
          }}
        >
          <input checked={checked} type={"checkbox"} className={checkedClassName} />
        </ResizableColumn>

        <ResizableColumn columnKey="lmcode" width={columnWidths.lmcode}>
          {translate('price.banner.lmcode')}
        </ResizableColumn>
        <ResizableColumn columnKey="description" width={columnWidths.description}>
          {translate('price.banner.description')}
        </ResizableColumn>
        <ResizableColumn columnKey="listName" width={columnWidths.listName}>
          {translate('price.banner.listname')}
        </ResizableColumn>
        <ResizableColumn columnKey="theme" width={columnWidths.theme}>
          {translate('price.banner.theme')}
        </ResizableColumn>
        <ResizableColumn columnKey="stores" width={columnWidths.stores}>
          {translate('price.banner.stores')}
        </ResizableColumn>

        <ResizableColumn
          columnKey="edit"
          width={columnWidths.edit}
          className='text-align-center right-side-icons'
          isActionColumn={true}
        >
          {!isSelectionMode && 'Editar'}
        </ResizableColumn>

        <ResizableColumn
          columnKey="delete"
          width={columnWidths.delete}
          className='text-align-center right-side-icons'
          isActionColumn={true}
        >
          {!isSelectionMode && translate('price.banner.delete')}
        </ResizableColumn>
      </tr>
    )
  }, [cdDynamicList, records, setCdDynamicList, translate, isSelectionMode, columnWidths, handleMouseDown, resizableStyles])

  const renderTheme = useCallback((record) => {
    if (record.theme && typeof record.theme === 'object' && record.theme.name) {
      return record.theme.name;
    }
    if (record.themeName && typeof record.themeName === 'string') {
      return record.themeName;
    }
    if (record.themeCode && typeof record.themeCode === 'string') {
      return record.themeCode;
    }
    if (record.theme_code && typeof record.theme_code === 'string') {
      return record.theme_code;
    }
    if (record.theme && typeof record.theme === 'string') {
      return record.theme;
    }
    return "-";
  }, []);

  const handleCheckboxChange = useCallback((productId, checked, record) => {
    if (checked) {
      removeSelected(productId);
    } else {
      if (addSelectedItem) {
        addSelectedItem(record);
      } else {
        setCdDynamicList([...cdDynamicList, productId]);
      }
    }
  }, [cdDynamicList, removeSelected, setCdDynamicList, addSelectedItem]);

  const handleScroll = useCallback((e) => {
    if (e.currentTarget.scrollTop > 20) {
      tableRef.current.classList.add("shadow-table-header-mission-block")
    } else {
      tableRef.current.classList.remove("shadow-table-header-mission-block")
    }
  }, []);

  const renderBody = useCallback(() => {
    if (!records || records.length === 0) {
      return (
        <tr>
          <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
            {translate('i18n.empity.list')}
          </td>
        </tr>
      );
    }

    const renderRecord = (record) => {
      const productId = record.cdProduct || record.id || record.cdPricebannerThemesList;
      const checked = cdDynamicList.includes(productId);
      const themeCode = record.themeCode
        || (record.theme && record.theme.code)
        || record.theme_code;
      const themeName = record.themeName
        || (record.theme && record.theme.name);
      const lm = record.lm || record.productLm;
      const isCmpmTheme = isCmpmThemeCode(themeCode) || themeName === 'Compre + Pague - Crédito';
      const isClubeLeroyTheme = isClubeLeroyThemeCode(themeCode) || themeName === 'Clube Leroy';
      const hasProductExtra = !!(record.cmpmExtra || (record.productExtrasMap && lm && record.productExtrasMap[lm]));
      const showEditPencil = hasProductExtra || isCmpmTheme || isClubeLeroyTheme;
      return (
        <tr className={`${checked ? 'stock-transfer-checked' : ''}`} key={productId}>
          <td
            className='text-align-center'
            style={{
              ...resizableStyles.cell,
              width: columnWidths.checkbox,
              minWidth: '15px',
              position: 'relative'
            }}
          >
            <input
              type="checkbox"
              className="stock-transfer-checkbox"
              checked={checked}
              onChange={() => handleCheckboxChange(productId, checked, record)}
            />
          </td>
          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.lmcode,
              minWidth: '15px',
              position: 'relative'
            }}
          >{record.lm || record.productLm || "-"}</td>

          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.description,
              minWidth: '15px',
              position: 'relative'
            }}
          >{record.description || record.productDescription || record.nameList || "-"}</td>

          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.listName,
              minWidth: '15px',
              position: 'relative'
            }}
          >{record.listName || record.nameList || "-"}</td>

          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.theme,
              minWidth: '15px',
              position: 'relative'
            }}
          >{renderTheme(record)}</td>

          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.stores,
              minWidth: '15px',
              position: 'relative'
            }}
          >{renderBranches(record)}</td>

          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.edit,
              minWidth: '15px',
              position: 'relative'
            }}
            className='mission-block-edit-icon text-align-center'
          >
            {!isSelectionMode && (
              <>
                {showEditPencil ? (
                  <Glyph
                    type='pencil'
                    color='primary'
                    onClick={() => {
                      setEditModalData(record);
                      setEditModalOpen(true);
                    }}
                  />
                ) : (
                  '-'
                )}
              </>
            )}
          </td>

          <td
            style={{
              ...resizableStyles.cell,
              width: columnWidths.delete,
              minWidth: '15px',
              position: 'relative'
            }}
            className='mission-block-edit-icon'
          >
            {!isSelectionMode && (
              <ExclusionModalPriceBanner
                t={translate}
                title={translate('price.banner.products.delete')}
                dynamicName={null}
                message={() => (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '18px' }}>{translate('price.banner.products.delete.message.singular')}</p>
                    <p style={{ fontSize: '14px' }}>
                      <strong>Produto:</strong><br />{record.description || record.productDescription}
                    </p>
                  </div>
                )}
                onConfirm={() => handleConfirmDelete(productId)}
                confirmButtonMsg={translate('confirm_button_label')}
                cancelButtonMsg={translate('close_button_label')}
                size="sm"
                renderButton={({ onClick }) => (
                  <i
                    onClick={onClick}
                    className='color-danger glyph glyph-trash-can icon-size pictogram-trash-icon'
                    style={{ cursor: 'pointer', display: 'block', justifyContent: 'center', textAlign: 'center', padding: '0px 8px' }}
                    aria-label={translate('price.banner.delete')}
                  />
                )}
                customDeleteMessageSingular={translate('price.banner.products.delete.message.singular')}
                customDeleteMessagePlural={translate('price.banner.products.delete.message.plural')}
                customDynamicLabel={translate('price.banner.delete.product.label')}
                itemCount={1}
              />
            )}
          </td>
        </tr>
      )
    }

    return records.map(renderRecord)
  }, [records, cdDynamicList, renderTheme, renderBranches, isSelectionMode, translate, handleConfirmDelete, handleCheckboxChange, columnWidths, resizableStyles])

  useScrollShadow(
    [modalOpen, modalData], 
    modalOpen ? '.branches-modal .custom-modal-content' : null
  )

  const renderModal = useCallback(() => {
    if (!modalOpen) return null;
    return (
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={translate('price.banner.stores')}
        confirmButtonText={translate('close_button_label')}
        hideCancelButton={true}
        className="branches-modal"
      >
        <ul>
          {modalData.map((item, index) => (
            <li key={index} style={{ marginBottom: '4px' }}>{item}</li>
          ))}
        </ul>
      </Modal>
    );
  }, [modalOpen, modalData, translate]);

  return (
    <>
      <style>{`
         .resize-handle {
           transition: none;
         }
         .resizable-header:hover .resize-icon {
           opacity: 1 !important;
         }
         .resize-handle:hover {  
           border-left: 3px solid #111;
         }
         .resize-handle:hover .resize-icon {
           opacity: 0 !important;
         }
         .resize-handle:active {
           border-left: none !important;
           background-color: transparent !important;
         }
        th[data-column="checkbox"],
        td[data-column="checkbox"] {
          width: 50px !important;
          min-width: 50px !important;
          max-width: 50px !important;
        }
        th.text-align-center.right-side-icons {
          text-align: center !important;
          padding: 21px 8px 6px 8px !important;
          cursor: default !important;
        }
        th.text-align-center.right-side-icons:hover {
          background-color: transparent !important;
          cursor: default !important;
        }
        th.text-align-center.right-side-icons:focus,
        th.text-align-center.right-side-icons:active,
        th.text-align-center.right-side-icons:focus-within {
          background-color: transparent !important;
          cursor: default !important;
          outline: none !important;
          box-shadow: none !important;
        }
        th.text-align-center.right-side-icons {
          pointer-events: none !important;
        }
        th.text-align-center.right-side-icons * {
          pointer-events: auto !important;
        }
        .table-scroll-wrapper {
          overflow-x: auto !important;
          overflow-y: hidden !important;
          width: 100% !important;
        }
         .table-scroll-wrapper tbody tr td,
         .table-scroll-wrapper thead tr th {
           overflow: hidden !important;
           text-overflow: ellipsis !important;
           white-space: nowrap !important;
           position: relative !important;
         }
       `}</style>
      <div className='table-scroll-wrapper'>
        <table
          cellPadding={0}
          cellSpacing={0}
          className={`mission-block-table-new new pictograms-table ${cdDynamicList.length ? 'pictograms-check-mode' : ''}`}
          style={resizableStyles.table}
        >
          <thead ref={tableRef}>{renderHeader()}</thead>
          <tbody
            ref={tbodyRef}
            className='tbody-mission-block'
            onScroll={handleScroll}
          >
            {renderBody()}
          </tbody>
        </table>
      </div>
      {renderModal()}
      <EditExtrasModal
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open);
          if (!open) onEditExtrasModalClosed?.();
        }}
        productData={editModalData}
        user={user}
        translate={translate}
        onSuccess={onEditExtrasSuccess || onRefresh}
      />
    </>
  )
}

TableProduct.propTypes = {
  cdDynamicList: PropTypes.array.isRequired,
  handleConfirmDelete: PropTypes.func.isRequired,
  records: PropTypes.array.isRequired,
  setCdDynamicList: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  removeSelected: PropTypes.func.isRequired,
  addSelectedItem: PropTypes.func,
  setSelectedItemsData: PropTypes.func,
  user: PropTypes.object,
  onRefresh: PropTypes.func,
  onEditExtrasSuccess: PropTypes.func,
  onEditExtrasModalClosed: PropTypes.func
}

export default TableProduct
